import React from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { destroyCookie } from 'nookies';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAuthContext } from '../Context/AuthContext/AuthContext';
import { AUTH_USER_FAILED, imgUrl } from '../Helpers/utils/Constant';
import Notifications from '../Components/Notifications/Notifications';
import { Link } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import NoticeShowHeader from '../Components/Notice/NoticeShowHeader';

const DashboardAppBar = ({ drawerWidth, handleDrawerToggle }: any) => {
  const queryClient = useQueryClient();
  const { user, dispatch: authDispatch } = useAuthContext();
  const { user_member_company_name, user_member_id } = user;
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );
  const [showShortfall, setShowShortfall] = React.useState(false);
  const popoverRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setShowShortfall(false);
      }
    };
    if (showShortfall) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShortfall]);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    destroyCookie(null, 'atab_ua');

    queryClient.clear();
    queryClient.resetQueries();
    // queryClient.resetMutationCache();
    authDispatch?.({
      type: AUTH_USER_FAILED,
    });
  };

  return (
    <>
      <AppBar
        position='fixed'
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#0c4da2',
          padding: '6px 20px',
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: '64px', sm: '64px' },
            px: { xs: 1.5, sm: 2.5 },
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            color='inherit'
            aria-label='open drawer'
            edge='start'
            onClick={handleDrawerToggle}
            sx={{ display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}></Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 1.5, md: 2 },
              }}
            >
              {user?.is_applied ? (
                <Box
                  ref={popoverRef}
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    component='span'
                    sx={{ display: 'inline-flex', alignItems: 'center' }}
                  >
                    <Badge
                      onClick={(e) => {
                        if (
                          user?.user_member_account_status?.toLowerCase() ===
                          'shortfall'
                        ) {
                          e.stopPropagation();
                          setShowShortfall((prev) => !prev);
                        }
                      }}
                      badgeContent={`${user?.user_member_account_status ===
                        'Application Inspection'
                        ? 'Forward for Office Inspection'
                        : user?.user_member_account_status === 'pending' &&
                          user?.resubmitted === 0
                          ? 'Submitted'
                          : user?.user_member_account_status === 'pending' &&
                            user?.resubmitted === 1
                            ? 'Resubmitted'
                            : user?.user_member_account_status
                              .charAt(0)
                              .toUpperCase() +
                            user.user_member_account_status.slice(1)
                        }`}
                      color={`${user?.user_member_account_status === 'active'
                        ? 'success'
                        : 'warning'
                        }`}
                      sx={{
                        '& .MuiBadge-badge': {
                          position: 'static',
                          transform: 'none',
                          width: 'fit-content',
                          height: 'auto',
                          minHeight: { xs: '24px', sm: '28px' },
                          padding: { xs: '4px 10px', sm: '6px 14px' },
                          fontSize: { xs: '10px', sm: '12px' },
                          lineHeight: 1.2,
                          textAlign: 'center',
                          whiteSpace: { xs: 'normal', sm: 'nowrap' },
                          maxWidth: { xs: '130px', sm: 'none' },
                          cursor:
                            user?.user_member_account_status?.toLowerCase() ===
                              'shortfall'
                              ? 'pointer'
                              : 'default',
                          ...(user?.user_member_account_status?.toLowerCase() ===
                            'shortfall' && {
                            backgroundColor: '#d32f2f',
                            color: '#ffffff',
                            border: '1.5px solid #1976d2',
                            animation: 'blink 1.5s infinite ease-in-out',
                          }),
                        },
                        '@keyframes blink': {
                          '0%': {
                            opacity: 1,
                            boxShadow:
                              '0 0 0 0 rgba(211, 47, 47, 0.6), 0 0 0 0 rgba(25, 118, 210, 0.4)',
                          },
                          '50%': {
                            opacity: 0.8,
                            boxShadow:
                              '0 0 0 4px rgba(211, 47, 47, 0), 0 0 0 6px rgba(25, 118, 210, 0.3)',
                          },
                          '100%': {
                            opacity: 1,
                            boxShadow:
                              '0 0 0 0 rgba(211, 47, 47, 0), 0 0 0 0 rgba(25, 118, 210, 0)',
                          },
                        },
                      }}
                    />
                  </Box>
                  {showShortfall &&
                    user?.user_member_account_status?.toLowerCase() ===
                    'shortfall' && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 'calc(100% + 12px)',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 1300,
                          p: 1.5,
                          borderRadius: '8px',
                          background:
                            'linear-gradient(135deg, #0b1a30 0%, #15305b 100%)',
                          border: '2px solid #d32f2f',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                          width: 260,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: -7,
                            left: '50%',
                            transform: 'translateX(-50%) rotate(45deg)',
                            width: 12,
                            height: 12,
                            backgroundColor: '#0b1a30',
                            borderLeft: '2px solid #d32f2f',
                            borderTop: '2px solid #d32f2f',
                            zIndex: -1,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: '#d32f2f',
                              }}
                            />
                            <Typography
                              variant='subtitle2'
                              sx={{
                                fontWeight: 'bold',
                                color: '#ef4444',
                                fontSize: '13px',
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                              }}
                            >
                              Shortfall Reason
                            </Typography>
                          </Box>
                          <IconButton
                            size='small'
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowShortfall(false);
                            }}
                            sx={{
                              color: '#ef4444',
                              p: 0,
                              '&:hover': {
                                color: '#ff7875',
                              },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                        <Typography
                          variant='body2'
                          sx={{
                            color: '#93c5fd',
                            fontSize: '12px',
                            lineHeight: 1.5,
                            fontWeight: 500,
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                          }}
                        >
                          {user?.shortfall_remarks || 'No remarks provided.'}
                        </Typography>
                      </Box>
                    )}
                </Box>
              ) : (
                <Badge
                  badgeContent={`${user?.user_member_account_status}`}
                  color={`warning`}
                  sx={{
                    '& .MuiBadge-badge': {
                      padding: '11px',
                      fontSize: '14px',
                      marginRight: '20px'
                    },
                  }}
                />
              )}

              <Box
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <NoticeShowHeader />
              </Box>

              <Box
                sx={{
                  cursor: 'pointer',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Notifications />
              </Box>
              <Box>
                <Tooltip title={user_member_company_name || ''}>
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar
                      alt='user'
                      src={
                        user?.company_owner_photo
                          ? `${imgUrl}/${user?.company_owner_photo}`
                          : '/static/images/avatar/2.jpg'
                      }
                    />
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id='menu-appbar'
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <Link to='/profile'>
                    <MenuItem onClick={() => handleCloseUserMenu()}>
                      <Typography textAlign='center'>Profile</Typography>
                    </MenuItem>
                  </Link>
                  {user_member_id && (
                    <MenuItem
                      onClick={() => {
                        handleCloseUserMenu();
                        handleLogout();
                      }}
                    >
                      <Typography textAlign='center'>Logout</Typography>
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default DashboardAppBar;
